import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/errors";
import { toast } from "@/shared/lib/toast";
import { Badge } from "@/shared/ui/badge";

import { useDetectTelegramChats, type TelegramChat } from "../api/use-detect-telegram-chats";
import { useSettings } from "../api/use-settings";
import { usePutSettings, useTestSend } from "../api/use-settings-mutations";
import { SEVERITY_INTENT } from "../lib/notif";
import type {
  NotificationSettings,
  NotificationRoutingRule,
  ChannelPreviews,
  ChannelTestResult,
} from "../lib/settings";
import { CHANNELS, channelLabelKey } from "../lib/settings";

import { ChannelPreviewModal } from "./ChannelPreviewModal";

function buildLocalPreview(
  row: NotificationRoutingRule,
  recipients: NotificationSettings["recipients"],
): ChannelPreviews {
  return {
    telegram: row.telegram
      ? `[${row.severity}] ${row.type} → ${recipients.telegram}`
      : "(channel disabled)",
    email: row.email ? `[${row.severity}] ${row.type} → ${recipients.email}` : "(channel disabled)",
  };
}

/** Inner form — mounts only once data is available; initializes local state from the initial snapshot. */
function SettingsForm({ initial }: { initial: NotificationSettings }): JSX.Element {
  const { t } = useTranslation("notifications");
  const putSettings = usePutSettings();
  const testSend = useTestSend();
  const detect = useDetectTelegramChats();

  const [local, setLocal] = useState<NotificationSettings>(() => structuredClone(initial));
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previews, setPreviews] = useState<ChannelPreviews | null>(null);
  const [results, setResults] = useState<ChannelTestResult[] | null>(null);
  const [testingType, setTestingType] = useState<string | null>(null);
  const [pickerChats, setPickerChats] = useState<TelegramChat[] | null>(null);

  function toggleChannel(type: string, channel: "telegram" | "email"): void {
    setLocal((prev) => ({
      ...prev,
      matrix: prev.matrix.map((row) =>
        row.type === type ? { ...row, [channel]: !row[channel] } : row,
      ),
    }));
  }

  function setThreshold(type: string, value: string): void {
    setLocal((prev) => ({
      ...prev,
      matrix: prev.matrix.map((row) => (row.type === type ? { ...row, threshold: value } : row)),
    }));
  }

  function setRecipient(channel: "telegram" | "email", value: string): void {
    setLocal((prev) => ({ ...prev, recipients: { ...prev.recipients, [channel]: value } }));
  }

  // Fill the telegram recipient with the chosen chat id and persist immediately.
  // We compute `next` explicitly (setLocal is async) so the save uses the new id.
  function applyChat(chat: TelegramChat): void {
    const next = { ...local, recipients: { ...local.recipients, telegram: chat.id } };
    setLocal(next);
    setPickerChats(null);
    putSettings.mutate(next, {
      onSuccess: () =>
        toast.success(t("settings.tg_detect_saved" as never, { title: chat.title || chat.id })),
    });
  }

  function handleDetect(): void {
    setPickerChats(null);
    detect.mutate(undefined, {
      onSuccess: (data) => {
        const chats = data?.chats ?? [];
        const [only] = chats;
        if (chats.length === 1 && only) {
          applyChat(only);
        } else if (chats.length > 1) {
          setPickerChats(chats);
        } else {
          toast.warn(t("settings.tg_detect_empty" as never));
        }
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 503) {
          toast.warn(t("settings.tg_detect_unavailable" as never));
        } else if (err instanceof ApiError && err.status === 409) {
          toast.error(t("settings.tg_detect_conflict" as never));
        } else {
          toast.error(t("settings.tg_detect_error" as never));
        }
      },
    });
  }

  function setMute(field: "from" | "to", value: string): void {
    setLocal((prev) => ({ ...prev, mute: { ...prev.mute, [field]: value } }));
  }

  function handlePreview(row: NotificationRoutingRule): void {
    setPreviews(buildLocalPreview(row, local.recipients));
    setResults(null);
    setPreviewOpen(true);
  }

  function handleTestSend(row: NotificationRoutingRule): void {
    setTestingType(row.type);
    testSend.mutate(row.type, {
      onSuccess: (res) => {
        if (res) {
          setPreviews(res.previews);
          setResults(res.results ?? null);
        }
        setPreviewOpen(true);
        toast.success(t("settings.test_sent" as never));
      },
      onSettled: () => setTestingType(null),
    });
  }

  function handleSave(): void {
    putSettings.mutate(local, {
      onSuccess: () => {
        toast.success(t("settings.saved" as never));
      },
    });
  }

  return (
    <div data-testid="settings-panel" className="space-y-6">
      {/* Matrix table */}
      <div className="overflow-x-auto rounded-xl border border-[var(--premium-border)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--premium-border)] bg-[var(--surface-sunken)]">
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">
                {t("settings.th_event" as never)}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">
                {t("settings.th_severity" as never)}
              </th>
              {CHANNELS.map((c) => (
                <th
                  key={c}
                  className="px-4 py-3 text-center text-xs font-semibold text-[var(--text-secondary)]"
                >
                  {t(channelLabelKey(c) as never)}
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">
                {t("settings.th_threshold" as never)}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--text-secondary)]">
                {t("settings.th_actions" as never)}
              </th>
            </tr>
          </thead>
          <tbody>
            {local.matrix.map((row) => {
              const eventKey = `settings.event_${row.type}` as never;
              const eventLabel = t(eventKey, { defaultValue: row.type });
              const intent = SEVERITY_INTENT[row.severity];
              return (
                <tr
                  key={row.type}
                  data-testid={`settings-row-${row.type}`}
                  className="border-b border-[var(--premium-border)] last:border-0 hover:bg-[var(--surface-sunken)]"
                >
                  {/* Event label */}
                  <td className="px-4 py-3 font-medium text-[var(--text-primary)]">{eventLabel}</td>

                  {/* Severity badge */}
                  <td className="px-4 py-3">
                    <Badge intent={intent} dot>
                      {t(`severity.${row.severity}` as never)}
                    </Badge>
                  </td>

                  {/* Channel toggles */}
                  {CHANNELS.map((c) => (
                    <td key={c} className="px-4 py-3 text-center">
                      <button
                        type="button"
                        data-testid={`settings-toggle-${row.type}-${c}`}
                        onClick={() => toggleChannel(row.type, c)}
                        aria-pressed={row[c]}
                        aria-label={`${t(channelLabelKey(c) as never)} – ${row.type}`}
                        className={[
                          "inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 transition-colors",
                          row[c]
                            ? "border-[var(--brand-600)] bg-[var(--brand-600)]"
                            : "border-[var(--premium-border-strong)] bg-[var(--canvas)]",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "pointer-events-none block h-4 w-4 rounded-full bg-white shadow transition-transform",
                            row[c] ? "translate-x-5" : "translate-x-0",
                          ].join(" ")}
                        />
                      </button>
                    </td>
                  ))}

                  {/* Threshold input */}
                  <td className="px-4 py-3">
                    <input
                      data-testid={`settings-threshold-${row.type}`}
                      value={row.threshold}
                      onChange={(e) => setThreshold(row.type, e.target.value)}
                      placeholder="—"
                      className="h-8 w-20 rounded-lg border border-[var(--premium-border-strong)] px-2 text-sm outline-none focus:border-[var(--brand-500)]"
                    />
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        data-testid={`settings-preview-${row.type}`}
                        onClick={() => handlePreview(row)}
                        className="rounded-lg border border-[var(--border-default)] px-2.5 py-1 text-xs font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-sunken)]"
                      >
                        {t("settings.preview" as never)}
                      </button>
                      <button
                        type="button"
                        data-testid={`settings-test-${row.type}`}
                        onClick={() => handleTestSend(row)}
                        disabled={testingType === row.type}
                        className="rounded-lg border border-[var(--brand-500)] px-2.5 py-1 text-xs font-semibold text-[var(--brand-600)] transition hover:bg-[var(--brand-50)] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {t("settings.test_send" as never)}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recipients */}
      <div className="space-y-4 rounded-xl border border-[var(--premium-border)] p-5">
        <div className="text-sm font-semibold text-[var(--text-primary)]">
          {t("settings.recipients" as never)}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {CHANNELS.map((c) => (
            <div key={c} className="flex flex-col gap-1.5">
              <label
                htmlFor={`settings-recipient-${c}`}
                className="text-xs font-medium text-[var(--text-secondary)]"
              >
                {t(`settings.${c}_recipient` as never)}
              </label>
              <input
                id={`settings-recipient-${c}`}
                data-testid={`settings-recipient-${c}`}
                value={local.recipients[c]}
                onChange={(e) => setRecipient(c, e.target.value)}
                placeholder={t(`settings.${c}_recipient_placeholder` as never, {
                  defaultValue: "",
                })}
                className="h-9 rounded-lg border border-[var(--premium-border-strong)] px-3 text-sm outline-none focus:border-[var(--brand-500)]"
              />
              {c === "telegram" && (
                <div className="space-y-2">
                  <button
                    type="button"
                    data-testid="detect-telegram-btn"
                    onClick={handleDetect}
                    disabled={detect.isPending}
                    className="h-9 rounded-lg border border-[var(--premium-border-strong)] px-3 text-sm text-[var(--text-primary)] transition hover:bg-[var(--surface-sunken)] disabled:opacity-50"
                  >
                    {detect.isPending
                      ? t("settings.tg_detect_detecting" as never)
                      : t("settings.tg_detect_btn" as never)}
                  </button>
                  {pickerChats && (
                    <div data-testid="telegram-chat-picker" className="space-y-1">
                      <p className="text-xs text-[var(--text-subtle)]">
                        {t("settings.tg_pick_title" as never)}
                      </p>
                      {pickerChats.map((chat) => (
                        <button
                          key={chat.id}
                          type="button"
                          data-testid={`telegram-chat-option-${chat.id}`}
                          onClick={() => applyChat(chat)}
                          className="block w-full rounded-lg border border-[var(--premium-border-strong)] px-3 py-1.5 text-left text-sm text-[var(--text-primary)] transition hover:bg-[var(--surface-sunken)]"
                        >
                          {(chat.title || chat.id) + " — " + chat.id}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <details
          data-testid="telegram-setup-guide"
          className="rounded-lg border border-[var(--premium-border)] bg-[var(--surface-sunken)] p-3"
        >
          <summary className="cursor-pointer text-xs font-semibold text-[var(--text-secondary)]">
            {t("settings.tg_guide_title" as never)}
          </summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-[var(--text-secondary)]">
            {(t("settings.tg_guide_steps", { returnObjects: true }) as unknown as string[]).map(
              (step, i) => (
                <li key={i}>{step}</li>
              ),
            )}
          </ol>
        </details>
      </div>

      {/* Mute window */}
      <div className="space-y-4 rounded-xl border border-[var(--premium-border)] p-5">
        <div className="text-sm font-semibold text-[var(--text-primary)]">
          {t("settings.mute_window" as never)}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="settings-mute-from"
              className="text-xs font-medium text-[var(--text-secondary)]"
            >
              {t("settings.mute_from" as never)}
            </label>
            <input
              id="settings-mute-from"
              data-testid="settings-mute-from"
              value={local.mute.from}
              onChange={(e) => setMute("from", e.target.value)}
              placeholder="HH:MM"
              className="h-9 w-24 rounded-lg border border-[var(--premium-border-strong)] px-3 text-sm outline-none focus:border-[var(--brand-500)]"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="settings-mute-to"
              className="text-xs font-medium text-[var(--text-secondary)]"
            >
              {t("settings.mute_to" as never)}
            </label>
            <input
              id="settings-mute-to"
              data-testid="settings-mute-to"
              value={local.mute.to}
              onChange={(e) => setMute("to", e.target.value)}
              placeholder="HH:MM"
              className="h-9 w-24 rounded-lg border border-[var(--premium-border-strong)] px-3 text-sm outline-none focus:border-[var(--brand-500)]"
            />
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          type="button"
          data-testid="settings-save"
          onClick={handleSave}
          disabled={putSettings.isPending}
          className="rounded-[10px] px-5 py-2.5 text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: "var(--grad-primary-btn)",
            boxShadow: "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5)",
          }}
        >
          {t("settings.save" as never)}
        </button>
      </div>

      {/* Channel preview modal */}
      <ChannelPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        previews={previews}
        results={results}
      />
    </div>
  );
}

/** Public shell — handles loading/error states, then delegates to SettingsForm. */
export function SettingsPanel(): JSX.Element {
  const { t } = useTranslation("notifications");
  const { data, isLoading, isError } = useSettings();

  if (isLoading) {
    return (
      <div data-testid="settings-panel">
        <p className="py-8 text-center text-sm text-[var(--text-subtle)]">{t("state.loading")}</p>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div data-testid="settings-panel">
        <p className="py-8 text-center text-sm text-[var(--intent-danger-fg)]">
          {t("state.error")}
        </p>
      </div>
    );
  }

  return <SettingsForm initial={data} />;
}
